import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ArrowBack, ContentCopy, PictureAsPdf } from '@mui/icons-material'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { apiTenant } from '../../../services/Tenant/ApiTenant'
import TenantStatusBadge from '../../../components/Tenant/TenantStatusBadge'
import CircularProgress from '@mui/material/CircularProgress'
import moment from 'moment'

const formatBRL = (centavos) => {
  return (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

const categoryLabels = {
  CONDO_FEE: 'Taxa de Condomínio',
  WATER: 'Água',
  GAS: 'Gás',
  ELECTRICITY: 'Energia',
  RENT: 'Aluguel',
  IPTU: 'IPTU',
  FIRE_INSURANCE: 'Seguro Incêndio',
  ADMIN_FEE: 'Taxa de Administração',
  INTEREST: 'Juros',
  FINE: 'Multa',
  DISCOUNT: 'Desconto',
}

const TenantInvoiceDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [invoice, setInvoice] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiTenant.get(`/tenant/invoices/${id}/`)
      .then(response => {
        setInvoice(response.data)
      })
      .catch(error => {
        console.error(error)
        enqueueSnackbar('Erro ao carregar fatura.', { variant: 'error' })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [id])

  const handleCopyDigitableLine = () => {
    const line = invoice?.bankslip?.digitable_line
    if (line) {
      navigator.clipboard.writeText(line)
        .then(() => enqueueSnackbar('Linha digitável copiada!', { variant: 'success' }))
        .catch(() => enqueueSnackbar('Erro ao copiar.', { variant: 'error' }))
    }
  }

  const monthNames = [
    '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ]

  if (loading) {
    return (
      <Container maxWidth={false}>
        <Box sx={{ marginY: '1em' }}>
          <Skeleton variant="rounded" height={500} animation="pulse"/>
        </Box>
      </Container>
    )
  }

  if (!invoice) {
    return (
      <Container maxWidth={false}>
        <Box sx={{ marginY: '2em', textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">Fatura não encontrada.</Typography>
          <Button startIcon={<ArrowBack/>} onClick={() => navigate('/area-cliente/faturas/')} sx={{ mt: 2 }}>
            Voltar para faturas
          </Button>
        </Box>
      </Container>
    )
  }

  return (
    <>
      <Helmet>
        <title>Fatura {invoice.number} - Portal do Cliente</title>
      </Helmet>
      <Container maxWidth={false}>
        <Box sx={{ marginY: '1em', pb: 1 }}>
          <Button startIcon={<ArrowBack/>} onClick={() => navigate('/area-cliente/faturas/')} sx={{ mb: 2 }}>
            Voltar para faturas
          </Button>
        </Box>

        <Card>
          <CardContent>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {invoice.number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {invoice.condo_name} - Unidade {invoice.unit_number}
                </Typography>
              </Box>
              <TenantStatusBadge status={invoice.status}/>
            </Box>

            <Divider sx={{ my: 2 }}/>

            {/* Info Grid */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Referência</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {invoice.month && invoice.year
                    ? `${monthNames[invoice.month]}/${invoice.year}`
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Vencimento</Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {invoice.due_date ? moment(invoice.due_date).format('DD/MM/YYYY') : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="text.secondary">Total</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatBRL(invoice.total)}
                </Typography>
              </Grid>
              {invoice.paid_at && (
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Pago em</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {moment(invoice.paid_at).format('DD/MM/YYYY')}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Fee Breakdown */}
            {invoice.fees?.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>Detalhamento</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'primary.main' }}>Categoria</TableCell>
                        <TableCell align="right" sx={{ color: 'primary.main' }}>Valor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.fees.map((fee) => (
                        <TableRow key={fee.id}>
                          <TableCell>{categoryLabels[fee.category] || fee.category}</TableCell>
                          <TableCell align="right">{formatBRL(fee.total)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', borderBottom: 'none' }}>Total</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', borderBottom: 'none' }}>
                          {formatBRL(invoice.total)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Bankslip Section */}
            {invoice.bankslip && (
              <>
                <Divider sx={{ my: 3 }}/>
                <Typography variant="h6" sx={{ mb: 2 }}>Boleto</Typography>

                {invoice.bankslip.digitable_line && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Linha Digitável
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={invoice.bankslip.digitable_line}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                      <Tooltip title="Copiar linha digitável">
                        <IconButton onClick={handleCopyDigitableLine} color="primary">
                          <ContentCopy/>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                )}

                {invoice.bankslip.due_date && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Vencimento do boleto: {moment(invoice.bankslip.due_date).format('DD/MM/YYYY')}
                  </Typography>
                )}

                {invoice.bankslip.url && (
                  <Button
                    variant="outlined"
                    startIcon={<PictureAsPdf/>}
                    component={Link}
                    href={invoice.bankslip.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Baixar PDF do Boleto
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </>
  )
}

export default TenantInvoiceDetail
